//src/app/features/questionnaires/show/PreviewQuestionnaire.tsx
import {
  DashboardContainerHeader,
  ScrollContainer,
} from 'app/layout/dashboardLayout/BoxLayout';
import { IUser } from 'app/services/user';
import { CancelIcon, PdfIcon } from 'assets/icons';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import PreviewExpandable from '../components/PreviewExpandable';
import PreviewPrepareQuestionnaire from '../create/PrepareQuestionnaire/preview';
import PreviewSituationAnalysis from '../create/TaxRequestTarget/preview';
import { selectPrepareQuestionnaire } from '../create/PrepareQuestionnaire/slice';
import { selectQuestionnaireRoutes } from '../edit/routes/slice';
import PreviewAdditionalQuestions from '../edit/TaxQuestionnaire/AdditionalQuestions/preview';
import PreviewBusinessInfo from '../edit/TaxQuestionnaire/BusinessInfo/preview';
import PreviewIncome from '../edit/TaxQuestionnaire/Income/preview';
import PreviewNonUSFinancialAccounts from '../edit/TaxQuestionnaire/NonUSFinancialAccounts/preview';
import PreviewNotes from '../edit/TaxQuestionnaire/Notes/preview';
import PreviewPersonalDetails from '../edit/TaxQuestionnaire/PersonalDetails/preview';
import PreviewSoleProprietorship from '../edit/TaxQuestionnaire/SoleProprietorship/preview';
import PreviewSpouseIncome from '../edit/TaxQuestionnaire/SpouseIncome/preview';
import PreviewSummary from '../edit/TaxQuestionnaire/Summary/preview';
import PreviewTaxesDeductions from '../edit/TaxQuestionnaire/TaxesDeductions/preview';
import { togglePreview } from '../edit/TaxQuestionnaire/togglePreviewSlice';
import PreviewAccountDetails from './AccountDetails/preview';
import {
  useGetProjectByIdQuery,
  useGetProjectByIdQueryState,
} from 'app/services/projects';
import { ProjectStatusBadge } from '../list/QuestionnairesTable/components/ProjectCard';
import Spinner from 'Components/Spinner';
import { IProject } from 'app/services/responseTypes';

export interface IPreviewQuestionnaireProps {
  showCloseBtn: boolean;
  isEdited?: boolean;
  preview?: boolean;
  clients?: IUser[] | undefined;
  taxReturn?: IProject;
}

export default function PreviewQuestionnaire(
  props: IPreviewQuestionnaireProps,
) {
  const { taxRequestType } = useSelector(selectPrepareQuestionnaire);
  const isIndividual = taxRequestType === 'individual';
  const isBusiness = taxRequestType === 'business';

  const routes = useSelector(selectQuestionnaireRoutes).routes;
  const { showCloseBtn, preview, isEdited, clients, taxReturn } = props;
  const dispatch = useDispatch();
  const componentRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname.includes('download')) {
      handlePrint();
    }
  }, []);

  return (
    <>
      <DashboardContainerHeader
        title={taxReturn?.title}
        subTitle={
          <>
            <p>{isEdited && !preview ? 'Edited after submission' : ''}</p>
            {taxReturn && (
              <div className="mt-2">
                <ProjectStatusBadge project={taxReturn} />
              </div>
            )}
          </>
        }
      >
        <div className="flex justify-center">
          <button
            onClick={handlePrint}
            className="focus:outline-none flex font-semibold justify-center items-center text-green text-sm hover:underline"
          >
            Save as PDF <PdfIcon className="ml-2 w-5" />
          </button>
          {!preview &&
            (showCloseBtn ? (
              <button
                className={`focus:outline-none px-5 bg-white text-gray hover:underline text-sm text-gray-light flex items-center outline-none`}
                onClick={() => dispatch(togglePreview())}
              >
                Close Preview
                <CancelIcon className="ml-2 w-5 text-gray-200" />
              </button>
            ) : (
              <button
                className={`focus:outline-none  px-5 bg-white text-gray hover:underline text-sm text-gray-light flex items-center outline-none `}
                onClick={() =>
                  navigate(`${location.pathname.replace('/questionnaire', '')}`)
                }
              >
                Close Preview
                <CancelIcon className="ml-2 w-5 text-gray-light" />
              </button>
            ))}
        </div>
      </DashboardContainerHeader>

      <ScrollContainer ref={componentRef}>
        <PreviewSituationAnalysis />
        <PreviewPrepareQuestionnaire />
        <PreviewAccountDetails clients={clients} />

        {isIndividual && (
          <>
            {routes.personal_details && <PreviewPersonalDetails />}
            {routes.income && <PreviewIncome />}
            {routes.nonUsFinancialAccounts && <PreviewNonUSFinancialAccounts />}
            {routes.spouseIncome && <PreviewSpouseIncome />}
            {routes.taxesDeductions && <PreviewTaxesDeductions />}
            {routes.notes && <PreviewNotes />}
          </>
        )}

        {isBusiness && (
          <>
            {routes.businessInfo && <PreviewBusinessInfo />}
            {routes.soleProprietorship && <PreviewSoleProprietorship />}
            {routes.additionalQuestions && <PreviewAdditionalQuestions />}
          </>
        )}
      </ScrollContainer>
    </>
  );
}
